import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import { styled, alpha } from '@mui/material/styles';
import { IoSearch } from 'react-icons/io5';
import InputBase from '@mui/material/InputBase';
import { motion } from 'framer-motion';
import Divider from '@mui/material/Divider';
import Storage from 'src/utils/storage/storage';
import {
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';
import Alert, { AlertColor } from '@mui/material/Alert';
import _ from 'lodash';
import { fetchScenarioDetails } from 'src/pages/analytics/simulator/home/api/simulator-module-api';
import { setScenario } from 'src/pages/analytics/simulator/home/store/simulator-home-slice';
import { getAccessConfigForScenario } from 'src/pages/analytics/simulator/home/store/simulator-home-async-thunks';
import SnackbarComponent from 'src/pages/analytics/simulator/innovation/components/snackbar/snackbar';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks';
import {
  getSearchResults,
  setSearchQueryInput
} from '../../../store/landing-slice';
import { IPageListItem } from '../../../types/landing-api-response.model';
import SearchListItem from './search-list-item';
import './index.scss';
import { minSearchLetters } from '../constant';

const SearchBarContainer = styled(motion.div)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(1),
  backgroundColor: '#ffffff',
  '&:focus': {
    backgroundColor: '#ffffff',
    outline: 'none',
    '&::placeholder': {
      opacity: 0
    }
  },
  width: '40%',
  // minWidth: '300px',
  height: '2em',
  boxShadow: '0 0 1px 0 rgba(0,0,0,0.31),0 3px 4px -2px rgba(0,0,0,0.25)'
}));

const SearchInputContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '2em',
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.spacing(1),
  position: 'relative',
  padding: '3px 6px'
}));

const SearchIcon = styled('span')`
  color: #bebebe;
  font-size: 16px;
  margin-right: 6px;
  margin-top: 6px;
  vertical-align: middle;
  color: #bebebe;
`;

const SearchInput = styled(InputBase)`
  .MuiInputBase-root {
    width: 100%;
    display: flex;
    flex-direction: row;
  }

  .MuiInputBase-input {
    width: 100%;
    height: 100%;
    outline: none;
    border: none;
    font-size: 16px;
    color: #12112e;
    font-weight: 500;
    border-radius: 6px;

    &:focus {
      outline: none;
      &::placeholder {
        opacity: 0;
      }
    }
    &::placeholder {
      color: #12112e;
      transition: all 250ms ease-in-out;
    }
  }
`;

const StyledDivider = styled(Divider)`
  width: 100%;
  min-height: 2px;
  background-color: ${({ theme }) => alpha(theme.palette.secondary.main, 0.35)};
`;
const SearchContent = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const SearchContentWrapper = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #12112e;
`;

const containerVariants = {
  expanded: {
    height: '15em',
    y: '6.5em',
    opacity: 1
  },
  collapsed: {
    height: '2em',
    y: 0,
    opacity: 0.5
  }
};

const containerTransition = { type: 'spring', damping: 22, stiffness: 150 };

function SearchBarInput() {
  const appDispatch = useAppDispatch();

  // getting the search results data from store
  const { isSearchDataLoading, searchResultsData, searchQueryInput } =
    useAppSelector((state) => state.landing);

  // getting the source id from storage
  const { source: sourceId } = Storage.getSourceId();

  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState(
    // Storage.getSearchQuery() ||
    ''
  );
  // local state for search container expanded/collapsed
  const [isExpanded, setExpanded] = useState(false);

  // local state for search results data
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  // local state for loading while navigating to scenario/page
  const [isSearchItemLoading, setIsSearchItemLoading] = useState(false);

  // local state for on hover search item
  const [hoveredItemId, setHoveredItemId] = useState(false);

  const inputRef = useRef();

  // local state for snackbar
  const [isSnackbarOpen, setSnackbar] = useState(false);

  const [snackbarContent, setSnackbarContent] = useState<{
    snackbarText: string;
    snackbarType: AlertColor;
  }>({
    snackbarText: 'Error fetching scenario data. Please try again.',
    snackbarType: 'error'
  });

  // Handler for snackbar
  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(false);
  };

  // hanlder for filtering search results
  const handleFilterSearchResults = (input) => {
    // checking for user query length >=3
    if (input.length >= minSearchLetters) {
      // getting the search results
      const suggestions = searchResultsData?.data?.filter((item: any) =>
        item.tags.some((tag) =>
          tag.toString().toLowerCase().includes(input.toLowerCase())
        )
      );
      setFilteredSuggestions(suggestions);
    } else {
      setFilteredSuggestions([]);
    }
  };

  // on api call success, calling handleFilterSearchResults
  useEffect(() => {
    // on api call success, using searchQueryInput from redux to get the updated query
    handleFilterSearchResults(searchQueryInput);
  }, [searchResultsData]);

  // handler for search click
  const handleSearchClick = () => {
    // expanding the search container
    setExpanded(true);
    // calling get search results api only when search container is not expanded
    if (!isExpanded) {
      appDispatch(getSearchResults({ sourceId })).then(() => {
        // calling handle change to filter search results as per the search query from redux
        handleFilterSearchResults(searchQueryInput);
      });
    }
  };

  // hanlder for clearing search query
  const clearSearchQuery = () => {
    // clearing the search query
    setSearchQuery('');
    // resetting the search results data
    setFilteredSuggestions([]);
  };

  // handler for search input
  const changeHandler = (e) => {
    const input = e.target.value.toLowerCase();
    // checking for empty search query
    if (input === '') {
      // setting search query to empty in local state, local storage and redux
      setSearchQuery('');
      Storage.setSearchQuery('');
      appDispatch(setSearchQueryInput(''));
    } else {
      appDispatch(setSearchQueryInput(input));
      // setting search query as per user input in local state, local storage and redux
      setSearchQuery(input);
      Storage.setSearchQuery(input);
      handleFilterSearchResults(input);
    }
  };

  // handler for collapse search container

  const collapseContainer = () => {
    setExpanded(false);
    // resetting the search results data
    setFilteredSuggestions([]);
  };

  // handler for navigating to page/scenario
  const handleNavigation = (searchItem: IPageListItem) => {
    // getting the current url endpoint
    const currEndpoint = window.location.pathname
      .split('/')
      .at(-1)
      .toLowerCase();
    // setting isSearchItem loading true
    setIsSearchItemLoading(true);

    // if route url is present then navigate to that respective PBI page
    if (searchItem.route_url) {
      navigate(searchItem.route_url);

      // setting isSearchItem loading false
      setIsSearchItemLoading(false);
      // closing the search container
      setExpanded(false);

      // clearing the search query after navigating to respective search item
      setSearchQuery('');
    }
    // if route url is not present then fetch the scenario details and navigate to the scenario
    else {
      // fetching the scenario details
      fetchScenarioDetails(sourceId, searchItem.id)
        .then((response) => {
          // eslint-disable-next-line dot-notation
          if (response.status === 200) {
            return response.data;
          }
          setSnackbar(true);

          // setting isSearchItem loading false
          setIsSearchItemLoading(false);

          // closing the search container
          setExpanded(false);

          // clearing the search query after navigating to respective search item
          setSearchQuery('');

          return null;
        })
        .then((scenario) => {
          appDispatch(setScenario(scenario));

          navigate(`/srm/simulator/${scenario.pk_id}/planner`);

          // setting isSearchItem loading false
          setIsSearchItemLoading(false);
          // closing the search container
          setExpanded(false);

          // clearing the search query after navigating to respective search item
          setSearchQuery('');

          return appDispatch(
            getAccessConfigForScenario({
              scenarioId: scenario.pk_id,
              sourceId
            })
          ).then((res) => {
            // checking if navigating on same page of scenario
            // if yes then reload the page with the selected scenario details
            if (currEndpoint === 'planner') window.location.reload();
          });
        })
        .catch((err) => {
          setSnackbar(true);
          // setting isSearchItem loading false
          setIsSearchItemLoading(false);
          // closing the search container
          setExpanded(false);

          // clearing the search query after navigating to respective search item
          setSearchQuery('');
        });
    }
  };

  return (
    <>
      <ClickAwayListener onClickAway={isExpanded ? collapseContainer : _.noop}>
        <SearchBarContainer
          animate={isExpanded ? 'expanded' : 'collapsed'}
          variants={containerVariants}
          transition={containerTransition}
          style={isSearchItemLoading ? { cursor: 'not-allowed' } : {}}
        >
          <SearchInputContainer
            className={isSearchItemLoading ? 'disabled-search-div' : ''}
          >
            <SearchIcon>
              <IoSearch />
            </SearchIcon>
            <SearchInput
              placeholder='Search…'
              // onFocus={expandContainer}
              onClick={handleSearchClick}
              ref={inputRef}
              value={searchQuery}
              onChange={changeHandler}
              // onKeyDown={onKeyDown}
              inputProps={{ 'aria-label': 'search' }}
              fullWidth
            />
            <IconButton
              onClick={clearSearchQuery}
              style={{ position: 'absolute', right: '0px' }}
            >
              <CloseIcon sx={{ fontSize: '16px', color: '#615f5f' }} />
            </IconButton>
          </SearchInputContainer>
          {isExpanded && <StyledDivider />}
          <SearchContent
            className={isSearchItemLoading ? 'disabled-search-div' : ''}
          >
            {/* when search bar is in focus and search query is empty */}
            {isExpanded && searchQuery === '' && (
              <SearchContentWrapper>
                {!isSearchDataLoading ? (
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#1E1E1E',
                      marginTop: '10px'
                    }}
                  >
                    Start typing to search
                  </div>
                ) : (
                  <div
                    style={{
                      overflow: 'hidden',
                      margin: 'auto',
                      marginTop: '10px'
                    }}
                  >
                    <CircularProgress />
                  </div>
                )}
              </SearchContentWrapper>
            )}
            {/* when search bar is in focus, search query has results */}
            {isExpanded &&
              searchQuery.length > 0 &&
              (!isSearchDataLoading ? (
                <List>
                  {filteredSuggestions.length > 0 ? (
                    filteredSuggestions.map((item) => (
                      <ListItem
                        key={item.id}
                        sx={{ padding: '4px 16px 4px 22px', color: '#1E1E1E' }}
                        className='search-item-div'
                        onMouseEnter={() => setHoveredItemId(item.id)}
                        onMouseLeave={() => setHoveredItemId(null)}
                      >
                        <ListItemText>
                          <SearchListItem
                            pageItem={item}
                            handleSearchItemNavigation={handleNavigation}
                            isSearchItemLoading={isSearchItemLoading}
                            isHovered={hoveredItemId === item.id}
                          />
                        </ListItemText>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography color='textSecondary'>
                            No results found
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              ) : (
                <div
                  style={{
                    overflow: 'hidden',
                    margin: 'auto',
                    marginTop: '10px'
                  }}
                >
                  <CircularProgress />
                </div>
              ))}
          </SearchContent>
        </SearchBarContainer>
      </ClickAwayListener>
      <SnackbarComponent
        isSnackbarOpen={isSnackbarOpen}
        setSnackbar={setSnackbar}
        snackbarText={snackbarContent.snackbarText}
        snackbarType={snackbarContent.snackbarType}
      />
    </>
  );
}

export default SearchBarInput;
